import { useEffect, useState } from "react";
import { X, ChevronRight, ChevronLeft, Wallet, Home, ShieldCheck, Layers } from "lucide-react";
import { formatRupiah } from "@/components/booking/bookingMeta";

// Choose WHAT to pay (amount + purpose) BEFORE picking a Duitku channel.
// All amounts are dynamic from the remaining obligations; backend re-validates.
export default function PaymentTypeModal({ open, booking, onClose, onSelect }) {
  const [custom, setCustom] = useState(false);
  const [amt, setAmt] = useState("");
  const [target, setTarget] = useState("room");

  // Reset internal step/state whenever the modal (re)opens for a booking,
  // so a previous "Bayar Nominal Lain" entry never leaks into the next open.
  useEffect(() => {
    if (open) { setCustom(false); setAmt(""); setTarget("room"); }
  }, [open, booking?.id]);

  if (!open || !booking) return null;

  const roomRem = Math.round(booking.room_amount_remaining || 0);
  const depRem = Math.round(booking.deposit_amount_remaining || 0);
  const roomPaid = Math.round(booking.room_amount_paid || 0);
  const dpRate = booking.dp_rate ?? 20;
  const isFirstRoom = roomPaid <= 0;
  const minDp = Math.ceil((booking.room_price_total || 0) * dpRate / 100);
  const dpAllowed = dpRate >= 20;

  const options = [];
  if (isFirstRoom && dpAllowed && minDp < roomRem) {
    options.push({ key: "dp", icon: Wallet, title: "Bayar DP", sub: `Minimal ${formatRupiah(minDp)}`, amount: minDp, purpose: "room" });
  }
  if (roomRem > 0 && dpAllowed) {
    options.push({ key: "room", icon: Home, title: "Lunasi Harga Kamar", sub: `Sisa ${formatRupiah(roomRem)}`, amount: roomRem, purpose: "room" });
  }
  if (roomRem > 0 && depRem > 0 && dpAllowed) {
    options.push({ key: "both", icon: Layers, title: "Harga Kamar + Deposit", sub: formatRupiah(roomRem + depRem), amount: roomRem + depRem, purpose: "room_and_deposit" });
  }
  if (roomRem <= 0 && depRem > 0) {
    options.push({ key: "dep", icon: ShieldCheck, title: "Bayar Deposit", sub: `Sisa ${formatRupiah(depRem)}`, amount: depRem, purpose: "deposit" });
  }

  // custom targets available
  const targets = [];
  if (roomRem > 0 && dpAllowed) targets.push({ value: "room", label: "Harga Kamar", max: roomRem });
  if (depRem > 0) targets.push({ value: "deposit", label: "Deposit", max: depRem });
  if (roomRem > 0 && depRem > 0 && dpAllowed) targets.push({ value: "room_and_deposit", label: "Harga Kamar + Deposit", max: roomRem + depRem });
  const activeTarget = targets.find((t) => t.value === target) || targets[0];

  const submitCustom = () => {
    const n = Math.round(Number(amt));
    if (!n || n <= 0) return;
    if (activeTarget && n > activeTarget.max) return;
    onSelect({ amount: n, purpose: activeTarget.value });
  };

  const nVal = Math.round(Number(amt) || 0);
  const overMax = activeTarget && nVal > activeTarget.max;
  const underMinDp = isFirstRoom && activeTarget?.value !== "deposit" && nVal > 0 && nVal < minDp && nVal < roomRem;
  const customError = overMax
    ? `Nominal melebihi maksimum ${formatRupiah(activeTarget.max)}`
    : underMinDp ? `Pembayaran pertama minimal DP ${formatRupiah(minDp)}` : null;

  const reset = () => { setCustom(false); setAmt(""); setTarget("room"); };
  const close = () => { reset(); onClose?.(); };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="payment-type-modal">
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-heading text-lg font-bold">{custom ? "Bayar Nominal Lain" : "Mau bayar apa?"}</h2>
          <button onClick={close} data-testid="payment-type-close" className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
        </div>

        <div className="p-4">
          {!custom ? (
            <div className="space-y-2" data-testid="payment-type-options">
              {options.map((o) => (
                <button key={o.key} onClick={() => onSelect({ amount: o.amount, purpose: o.purpose })}
                  data-testid={`payment-type-${o.key}`}
                  className="w-full flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-3 text-left hover:border-primary hover:bg-secondary/40 transition-colors">
                  <span className="grid place-items-center w-9 h-9 rounded-lg bg-secondary text-primary"><o.icon size={18} /></span>
                  <span className="flex-1">
                    <span className="block text-sm font-semibold">{o.title}</span>
                    <span className="block text-xs text-muted-foreground">{o.sub}</span>
                  </span>
                  <span className="font-heading text-sm font-bold text-primary">{formatRupiah(o.amount)}</span>
                </button>
              ))}
              {targets.length > 0 && (
                <button onClick={() => { setTarget(targets[0].value); setCustom(true); }}
                  data-testid="payment-type-custom"
                  className="w-full flex items-center gap-3 rounded-xl border border-dashed border-border bg-background px-4 py-3 text-left hover:border-primary transition-colors">
                  <span className="grid place-items-center w-9 h-9 rounded-lg bg-secondary text-primary"><Wallet size={18} /></span>
                  <span className="flex-1 text-sm font-semibold">Bayar Nominal Lain</span>
                  <ChevronRight size={16} className="text-muted-foreground" />
                </button>
              )}
              {options.length === 0 && targets.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">Tidak ada kewajiban yang perlu dibayar.</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Tujuan Pembayaran</label>
                <div className="mt-2 space-y-2">
                  {targets.map((t) => (
                    <label key={t.value} data-testid={`payment-target-${t.value}`}
                      className={`flex items-center gap-3 rounded-xl border px-3 py-2 cursor-pointer transition-colors ${target === t.value ? "border-primary bg-secondary/40" : "border-border"}`}>
                      <input type="radio" name="target" checked={target === t.value} onChange={() => setTarget(t.value)} className="accent-primary" />
                      <span className="flex-1 text-sm font-medium">{t.label}</span>
                      <span className="text-xs text-muted-foreground">maks {formatRupiah(t.max)}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Nominal (Rp)</label>
                <input type="number" min="1" value={amt} onChange={(e) => setAmt(e.target.value)}
                  data-testid="payment-custom-amount"
                  placeholder={isFirstRoom && activeTarget?.value !== "deposit" ? `Minimal DP ${formatRupiah(minDp)}` : "Masukkan nominal"}
                  className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                {isFirstRoom && activeTarget?.value !== "deposit" && (
                  <p className="mt-1 text-xs text-muted-foreground">Pembayaran pertama minimal DP {formatRupiah(minDp)}.</p>
                )}
                {customError && <p className="mt-1 text-xs text-rose-600" data-testid="payment-custom-error">{customError}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setCustom(false)} className="flex-1 py-2.5 rounded-full text-sm font-semibold border border-border hover:bg-secondary/60 transition-colors">Kembali</button>
                <button onClick={submitCustom} disabled={!amt || nVal <= 0 || !!customError}
                  data-testid="payment-custom-submit"
                  className="flex-1 py-2.5 rounded-full text-sm font-semibold bg-accent text-accent-foreground hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 disabled:hover:scale-100">
                  Lanjut Pilih Metode
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
